import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Sprout, 
  Droplets, 
  MapPin, 
  Package, 
  Calendar,
  CheckCircle,
  Share2,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { Breadcrumb } from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

import { BACKEND_URL, PHP_API } from '../config/api';

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${PHP_API.product}?id=${id}`);
      setProduct(response.data);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: product.name,
        text: product.short_description,
        url: window.location.href,
      });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="font-heading font-bold text-2xl text-stone-900 mb-4">Product Not Found</h2>
        <p className="text-stone-600 mb-6">The product you're looking for doesn't exist.</p>
        <Link to="/products">
          <Button className="btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Button>
        </Link>
      </div>
    );
  }

  const imageUrl = product.image_url 
    ? (product.image_url.startsWith('http') ? product.image_url : `${BACKEND_URL}${product.image_url}`)
    : 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=800';

  const specs = [
    { label: 'Germination Rate', value: product.germination_rate, icon: Sprout },
    { label: 'Suitable Season', value: product.suitable_season, icon: Calendar },
    { label: 'Region Suitability', value: product.region_suitability, icon: MapPin },
    { label: 'Packaging Size', value: product.packaging_size, icon: Package },
  ];

  return (
    <div className="page-transition min-h-screen bg-stone-50">
      {/* Header */}
      <section className="bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb 
            items={[
              { label: 'Products', path: '/products' },
              { label: product.name }
            ]} 
          />
        </div>
      </section>

      {/* Product Details */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-medium bg-white">
                <img
                  src={imageUrl}
                  alt={product.name}
                  className="w-full h-[500px] object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=800';
                  }}
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-full">
                    {product.crop_type}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-4" data-testid="product-name">
                {product.name}
              </h1>
              
              <p className="text-stone-600 text-lg mb-8 leading-relaxed">
                {product.short_description}
              </p>

              {/* Specifications Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {specs.map((spec) => {
                  const Icon = spec.icon;
                  return (
                    <div key={spec.label} className="bg-white rounded-xl p-4 shadow-soft">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <span className="text-stone-500 text-sm">{spec.label}</span>
                      </div>
                      <p className="font-semibold text-stone-900 pl-[52px]">{spec.value}</p>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/dealer-inquiry" className="flex-1 sm:flex-none">
                  <Button className="btn-primary w-full sm:w-auto" data-testid="inquiry-btn">
                    Inquire Now
                  </Button>
                </Link>
                <Button variant="outline" onClick={handleShare} className="btn-outline" data-testid="share-btn">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>

              {/* Key Features */}
              <div className="bg-primary/5 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg text-stone-900 mb-4">Key Features</h3>
                <ul className="space-y-3">
                  {[
                    'High germination rate guaranteed',
                    'Disease resistant variety',
                    'Optimal yield potential',
                    'Quality certified seeds',
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-stone-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>

          {/* Detailed Description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-16"
          >
            <div className="bg-white rounded-2xl p-8 shadow-soft">
              <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">Product Description</h2>
              <div className="prose max-w-none text-stone-600 leading-relaxed">
                <p>{product.detailed_description}</p>
              </div>
            </div>
          </motion.div>

          {/* Back Button */}
          <div className="mt-12">
            <Link to="/products">
              <Button variant="outline" className="btn-outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


